import React from "react";
import { Logo, TamFunnel } from "./shared";
import * as m from "../lib/model";
import { qsbsBump } from "./room/Qsbs";

const Row = ({ k, v }) => (<li><span className="k">{k}</span><span className="v">{v}</span></li>);

// Multi-section Confidential Information Memorandum, rendered from the live deal model.
// `sec` is a map of sectionId -> boolean (what to include).
export const CIMDoc = React.forwardRef(({ M, sec, displayName, industry, location, overview, market }, ref) => {
  const name = displayName || M.dealName || M.companyName || "Confidential Opportunity";
  const ind = industry || M.industry || "operating business";
  const loc = location || M.address || "United States";
  const ex = m.exitReturns(M);
  const wf = m.lpWaterfall(M);
  const bump = qsbsBump(M);
  const dscr = m.dscrYr1(M);
  const senior = m.loanAmt(M);
  const holdLabel = M.holdIndefinite ? "Indefinite (cash-flow hold)" : `${M.hold} years`;

  const hist = (M.historicalYears || []).slice(-3);
  const g = 1 + (M.sdeGrowth || 0) / 100;
  const last = hist[hist.length - 1] || { revenue: M.ttmRevenue, gm: M.grossMarginPct, netIncome: 0, sde: M.sde };
  const forecast = { year: "FY+1 (F)", revenue: Math.round((last.revenue || 0) * g), gm: last.gm || M.grossMarginPct, netIncome: Math.round((last.netIncome || 0) * g), sde: Math.round((M.sde || 0) * g), isF: true };
  const finCols = [...hist, forecast];

  const uses = [["Purchase price (enterprise value)", M.askPrice]];
  const totalUses = M.askPrice || 0;
  const sources = [
    ["Senior debt", senior],
    ["Seller note", M.snAmt || 0],
    ["Sponsor / investor equity", ex.equityInvested],
  ].filter((r) => r[1] > 0);

  const R = { ...m.DEFAULT_ROLLUP, ...(M.rollup || {}) };
  const ro = m.rollupModel(R);
  const hasRollup = (ro.rows || []).length > 0;

  const mult = M.sde ? m.nac(M) / M.sde : 0;
  const strengths = [
    ["Consistent top-line growth", `Revenue reached ${m.fmt$(M.ttmRevenue)} over the review period, signalling durable demand.`],
    ["Attractive entry multiple", `Entry at ${m.fmtX(mult)} on SDE${M.slbOn ? " net of sale-leaseback proceeds" : ""} — at or below sector comps.`],
    m.grossMargin(M) > 0 ? ["Healthy gross margins", `Gross margin of ${m.fmtPct(m.grossMargin(M))} leaves room to absorb owner-salary normalisation.`] : null,
    M.snAmt > 0 ? ["Seller alignment", `A ${m.fmt$(M.snAmt)} seller note aligns the seller and reduces senior leverage.`] : null,
    ["Established platform", `Founded ${M.yearEstablished || "—"}${M.employeeCount ? `, ${M.employeeCount} employees` : ""} — a proven, staffed operation.`],
  ].filter(Boolean);
  const weaknesses = [
    ["SDE add-back concentration", `True operating earnings after a market salary are ≈ ${m.fmt$(m.trueEarnings(M))}.`],
    ["Debt service coverage", `Year-1 DSCR of ${m.fmtX(dscr)}${dscr < 1.3 ? " — below the 1.30× comfort threshold" : " — adequate"}.`],
    ["Owner dependency", `Key-person risk is elevated; crew and customer retention through transition is critical.`],
    ["Customer concentration", `Diligence must confirm no single customer dominates the ${m.fmt$(M.ttmRevenue)} top line.`],
  ];
  const comps = (market && Array.isArray(market.comps)) ? market.comps.slice(0, 6) : [];

  return (
    <div className="teaser cim" data-testid="cim-doc" ref={ref}>
      {sec.cover && (
        <div className="cim-cover">
          <Logo height={64} />
          <div className="cim-cover-eyebrow">Confidential Information Memorandum</div>
          <h1 className="cim-cover-title">{name}</h1>
          <div className="cim-cover-sub">{ind} · {loc}</div>
          <div className="cim-cover-meta">
            Prepared by {M.analystName || "XYZ Ventures"}{M.analystContact ? ` · ${M.analystContact}` : ""}<br />
            {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </div>
          <div className="cim-cover-nda">Strictly confidential — distributed under NDA. Not an offer to sell securities.</div>
        </div>
      )}

      <div className="cim-body">
        {sec.summary && (
        <div className="t-sec">
          <h2>Executive Summary</h2>
          <p>{overview || `An established ${String(ind).toLowerCase()} operator in ${loc} with durable, largely recurring revenue and a clean owner-transition opportunity. The business is being offered at ${m.fmt$(M.askPrice)} (${(M.askPrice && M.sde ? (M.askPrice / M.sde).toFixed(1) : "—")}× adjusted SDE of ${m.fmt$(M.sde)}), financed with senior debt and sponsor equity, targeting a modeled LP return of ${m.fmtX(wf.lpMoic)} / ${isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)} over a ${holdLabel} hold.`}</p>
          <div className="cim-tiles">
            <div className="cim-tile"><div className="lbl">Asking price</div><div className="num">{m.fmt$(M.askPrice)}</div></div>
            <div className="cim-tile"><div className="lbl">Adj. SDE / EBITDA</div><div className="num">{m.fmt$(M.sde)}</div></div>
            <div className="cim-tile"><div className="lbl">Entry multiple</div><div className="num">{M.askPrice && M.sde ? (M.askPrice / M.sde).toFixed(1) + "×" : "—"}</div></div>
            <div className="cim-tile"><div className="lbl">Year-1 DSCR</div><div className="num">{m.fmtX(dscr)}</div></div>
            <div className="cim-tile"><div className="lbl">Modeled LP MOIC</div><div className="num">{m.fmtX(wf.lpMoic)}</div>{bump ? <div className="sub">QSBS +{m.fmtX(bump.x)} after tax</div> : null}</div>
            <div className="cim-tile"><div className="lbl">Modeled LP IRR</div><div className="num">{isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)}</div>{bump && bump.bps > 0 ? <div className="sub">QSBS +{bump.bps} bps after tax</div> : null}</div>
          </div>
        </div>
      )}

      {sec.business && (
        <div className="t-sec">
          <h2>Business Overview</h2>
          <ul className="t-info">
            <Row k="Industry" v={ind} />
            <Row k="Headquarters" v={loc} />
            {M.yearEstablished ? <Row k="Founded" v={M.yearEstablished} /> : null}
            {M.employeeCount ? <Row k="Employees" v={M.employeeCount} /> : null}
            <Row k="Deal type" v={M.dealType} />
            <Row k="Hold period" v={holdLabel} />
          </ul>
        </div>
      )}

      {sec.financials && (
        <div className="t-sec">
          <h2>Financial Snapshot</h2>
          <div className="t-fin-wrap">
            <table className="t-fin">
              <thead><tr><th>$</th>{finCols.map((c) => <th key={c.year} className={c.isF ? "fc" : ""}>{c.year}</th>)}</tr></thead>
              <tbody>
                <tr><td>Revenue</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmt$(c.revenue)}</td>)}</tr>
                <tr><td>Gross margin</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmtPct(m.pctOf(c.gm))}</td>)}</tr>
                <tr><td>Net income</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmt$(c.netIncome)}</td>)}</tr>
                <tr className="hl"><td>Adj. SDE / EBITDA</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmt$(c.sde)}</td>)}</tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sec.structure && (
        <div className="t-grid t-grid-a">
          <div className="t-sec"><h2>Uses of Funds</h2>
            <ul className="t-terms">
              {uses.map(([k, v]) => <Row key={k} k={k} v={m.fmt$(v)} />)}
              <li className="hl"><span className="k">Total uses</span><span className="v">{m.fmt$(totalUses)}</span></li>
            </ul>
          </div>
          <div className="t-sec"><h2>Sources of Funds</h2>
            <ul className="t-terms">
              {sources.map(([k, v]) => <Row key={k} k={k} v={m.fmt$(v)} />)}
              <li className="hl"><span className="k">Total sources</span><span className="v">{m.fmt$(sources.reduce((a, r) => a + r[1], 0))}</span></li>
            </ul>
          </div>
        </div>
      )}

      {sec.financing && (
        <div className="t-sec"><h2>Financing — Senior Debt</h2>
          <ul className="t-terms">
            <Row k="Senior debt amount" v={m.fmt$(senior)} />
            <Row k="Interest rate" v={`${M.rate}%`} />
            <Row k="Amortization / term" v={`${M.loanYrs} years`} />
            <Row k="Year-1 DSCR" v={m.fmtX(dscr)} />
            {M.snAmt > 0 ? <Row k="Seller note" v={`${m.fmt$(M.snAmt)} @ ${M.snRate}%`} /> : null}
          </ul>
        </div>
      )}

      {sec.returns && (
        <div className="t-sec"><h2>Returns &amp; Exit</h2>
          <ul className="t-terms">
            <Row k="Sponsor / investor equity" v={m.fmt$(ex.equityInvested)} />
            <Row k="Preferred return" v={`${M.prefReturn}% cumulative`} />
            <Row k="Modeled LP MOIC" v={`${m.fmtX(wf.lpMoic)}${bump ? ` · QSBS +${m.fmtX(bump.x)} after tax` : ""}`} />
            <Row k="Modeled LP IRR" v={isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)} />
            <Row k="Exit SDE / EBITDA" v={m.fmt$(ex.sdeExit)} />
            <Row k="Exit enterprise value" v={M.holdIndefinite ? "No exit — cash-flow hold" : `${m.fmt$(ex.exitEV)} (${M.exitMult}×)`} />
            <Row k="Equity at exit" v={M.holdIndefinite ? "No exit — cash-flow hold" : m.fmt$(ex.equityAtExit)} />
            {ex.rollOwnPct > 0.01 ? <Row k="Seller rollover (common only)" v={`${m.fmtPct(ex.rollOwnPct)} of equity — ${m.fmt$(ex.rollExit)} of the exit residual, after the LP pref`} /> : null}
          </ul>
        </div>
      )}

      {sec.rollup && hasRollup && (
        <div className="t-sec"><h2>Roll-Up / Buy-and-Build Opportunity</h2>
          <p style={{ marginBottom: 10 }}>A platform buy-and-build consolidating {ro.rows.length} businesses at a blended entry of {ro.blendedEntry.toFixed(1)}× into a single entity exiting at {ro.exitMultiple}× — {m.fmt$(ro.multipleArb * ro.totEbitda)} of value from multiple arbitrage.</p>
          <div className="t-fin-wrap">
            <table className="t-fin">
              <thead><tr><th>Target</th><th>EBITDA</th><th>Multiple</th><th>Price</th><th>Yr</th></tr></thead>
              <tbody>
                {ro.rows.map((r, i) => <tr key={i}><td style={{ textAlign: "left" }}>{r.name}</td><td>{m.fmt$(r.ebitda)}</td><td>{r.multiple.toFixed(1)}×</td><td>{m.fmt$(r.price)}</td><td>{r.year}</td></tr>)}
                <tr className="hl"><td style={{ textAlign: "left" }}>Consolidated</td><td>{m.fmt$(ro.totEbitda)}</td><td>{ro.blendedEntry.toFixed(1)}×</td><td>{m.fmt$(ro.totPrice)}</td><td></td></tr>
              </tbody>
            </table>
          </div>
          <ul className="t-terms" style={{ marginTop: 12 }}>
            <Row k="Consolidated EBITDA" v={m.fmt$(ro.totEbitda)} />
            <Row k="Sponsor equity in" v={m.fmt$(ro.totSponsorEquity)} />
            <Row k="Exit enterprise value" v={`${m.fmt$(ro.exitEV)} (${ro.exitMultiple}×)`} />
            <Row k="Sponsor proceeds at exit" v={m.fmt$(ro.sponsorProceeds)} />
            <Row k="Platform MOIC / IRR" v={`${m.fmtX(ro.moic)} / ${isNaN(ro.irr) ? "n/a" : m.fmtPct(ro.irr)}`} />
          </ul>
        </div>
      )}

      {sec.strengths && (
        <div className="t-sec"><h2>Strengths</h2>
          <ul className="cim-sw">{strengths.map(([h, d]) => <li key={h}><b>{h}.</b> {d}</li>)}</ul>
        </div>
      )}

      {sec.weaknesses && (
        <div className="t-sec"><h2>Weaknesses &amp; Key Risks</h2>
          <ul className="cim-sw">{weaknesses.map(([h, d]) => <li key={h}><b>{h}.</b> {d}</li>)}</ul>
        </div>
      )}

      {sec.market && (
        <div className="t-sec"><h2>Market Intelligence &amp; Comps</h2>
          {market ? (
            <>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {market.sector && <span className="tag" style={{ fontSize: 9 }}>Sector: {market.sector}</span>}
                {market.typicalMultiples?.range && <span className="tag" style={{ fontSize: 9 }}>Typical: {market.typicalMultiples.range}</span>}
              </div>
              {market.marketOverview && <p>{market.marketOverview}</p>}
              {market.tam && <TamFunnel tam={market.tam} testid="cim-tam-funnel" />}
              {comps.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div className="cim-comps-title">Comparable Transactions</div>
                  <div className="t-fin-wrap">
                    <table className="t-fin cim-comps">
                      <thead><tr><th style={{ textAlign: "left", width: "42%" }}>Comparable</th><th style={{ width: "22%" }}>Location</th><th style={{ width: "16%" }}>Revenue</th><th style={{ width: "14%" }}>Multiple</th><th style={{ width: "6%" }}>Yr</th></tr></thead>
                      <tbody>
                        {comps.map((c, i) => <tr key={i}><td style={{ textAlign: "left" }}>{c.descriptor}</td><td>{c.location || "—"}</td><td>{c.revenue ? m.fmt$(c.revenue) : "—"}</td><td>{c.multiple || "—"}</td><td>{c.year || "—"}</td></tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {Array.isArray(market.consolidators) && market.consolidators.length > 0 && (
                <ul className="cim-sw" style={{ marginTop: 8 }}>{market.consolidators.slice(0, 6).map((c, i) => <li key={i}><b>{c.name}</b>{c.type ? ` (${c.type})` : ""}. {c.note}</li>)}</ul>
              )}
            </>
          ) : (
            <p style={{ color: "#888" }}>Run “Generate Market Comps” on the AI Evaluation page to include live sector comps, consolidators and exits here.</p>
          )}
        </div>
      )}

      </div>
      <div className="t-foot">XYZ Ventures · {ind} · {loc} · Confidential — available under NDA · Not an offer to sell securities</div>
    </div>
  );
});
